// Ruta: packages/auth/src/token-storage.ts
// Almacenamiento seguro del refresh token usando Web Crypto API.
// - access_token: nunca persiste (solo en memoria via Zustand)
// - refresh_token: cifrado con AES-GCM usando Web Crypto API

const STORAGE_KEY = 'hs_rt'

// Derive a CryptoKey from the app encryption key env var
async function getEncryptionKey(): Promise<CryptoKey> {
  const rawKey = (typeof window !== 'undefined'
    ? (import.meta as unknown as { env: Record<string, string> }).env?.VITE_TOKEN_ENCRYPTION_KEY
    : undefined) ?? 'heyshaker-dev-key-fallback-32chars!'

  const keyMaterial = await window.crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(rawKey.slice(0, 32).padEnd(32, '0')),
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt']
  )
  return keyMaterial
}

async function encrypt(text: string): Promise<string> {
  const key = await getEncryptionKey()
  const iv = window.crypto.getRandomValues(new Uint8Array(12))
  const encoded = new TextEncoder().encode(text)

  const ciphertext = await window.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded)

  // Combine iv + ciphertext and encode as base64
  const combined = new Uint8Array(iv.byteLength + ciphertext.byteLength)
  combined.set(iv, 0)
  combined.set(new Uint8Array(ciphertext), iv.byteLength)
  return btoa(String.fromCharCode(...combined))
}

async function decrypt(encoded: string): Promise<string | null> {
  try {
    const key = await getEncryptionKey()
    const combined = Uint8Array.from(atob(encoded), (c) => c.charCodeAt(0))
    const iv = combined.slice(0, 12)
    const ciphertext = combined.slice(12)

    const decrypted = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      ciphertext
    )
    return new TextDecoder().decode(decrypted)
  } catch {
    return null
  }
}

export const tokenStorage = {
  async saveRefreshToken(token: string): Promise<void> {
    try {
      const encrypted = await encrypt(token)
      localStorage.setItem(STORAGE_KEY, encrypted)
    } catch {
      // If crypto fails, do not persist (fail safe)
    }
  },

  async getRefreshToken(): Promise<string | null> {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return null
    return decrypt(stored)
  },

  clearRefreshToken(): void {
    localStorage.removeItem(STORAGE_KEY)
  },
}
